import {Inject, Injectable} from '@angular/core';
import {APP_CONFIG, AppConfig} from '@src/app.config';
import {RequestService} from '@shared/services/http/request/request.service';
import {map, Observable} from 'rxjs';
import {IdentityUser} from '@app/areas/users/models/identity-user.interface';
import {UserProfile} from '@app/areas/account/user-profile.interface';
import {
  EnquiryUserUpdateInputDto
} from "@app/areas/crm/enquiries/components/update-enquiry-user-form/update-enquiry-user-form.component";

export enum EnquiryStatus {
  OPEN = 0,
  PATIENT_SUBMITTED = 1,
  CLOSED = 3,
}

export interface EnquiryNote {
  id: string;
  createdBy: string;
  content: string;
  systemNote: string;
  createdAt: number;
}

export interface EnquiryDetail {
  enquiry: Enquiry;
  identityUser?: IdentityUser;
  userProfile?: UserProfile;
  assignedToUserProfile?: UserProfile;
  assignedToIdentityProfile?: UserProfile;
}

export interface Enquiry {
  id: string;
  userId?: string;
  status: EnquiryStatus;
  region: string;
  role: 'patient' | 'loved_one' | 'practitioner';
  preferredLanguage?: string;
  contactPhone?: string;
  contactEmail?: string;
  assignedTo?: string;
  appointmentId?: string;
  createdAt: number;
  updatedAt: number;
  metadata: { [key: string]: string };
  lastNote?: EnquiryNote;
}

export interface ListEnquiriesRequestDto {
  status?: EnquiryStatus;
  region?: string;
  assignedTo?: string;
  role?: Enquiry['role'];
  pageSize?: number;
  pageToken?: string;
}

export interface ListEnquiriesResponseDto {
  enquiries: any[]; // raw from server (proto/DTO)
  nextPageToken?: string;
}

export interface CreateEnquiryDto {
  userId?: string;
  status?: EnquiryStatus;
  region: string;
  role: Enquiry['role'];
  preferredLanguage?: string;
  contactPhone?: string;
  contactEmail?: string;
  assignedTo?: string;
}

export interface UpdateEnquiryDto {
  enquiry: Partial<CreateEnquiryDto> & { id: string };
  updateMask?: { paths: string[] };
}

export interface CreateUserDto {
  enquiryId: string;
  phoneNumber: string;
  region: string;
  roles: string[];
}

@Injectable({providedIn: 'root'})
export class EnquiriesApiService {
  private base = `${this.appConfig.angularEndpoint}/enquiries`;

  constructor(
    private request: RequestService,
    @Inject(APP_CONFIG) private appConfig: AppConfig,
  ) {
  }

  // ---------- API ----------

  getByUser(userId: string): Observable<Enquiry> {
    return this.request.get(`${this.base}/byUser/${userId}`).pipe(
      map(r => (r.body as Enquiry) ?? null),
    );
  }

  listNotes(enquiryId: string): Observable<EnquiryNote[]> {
    return this.request.get(`${this.base}/${enquiryId}/notes`).pipe(
      map(r => (r.body as EnquiryNote[]) ?? []),
    );
  }

  list(q: ListEnquiriesRequestDto): Observable<{ items: Enquiry[]; nextPageToken?: string }> {
    const params = new URLSearchParams();
    if (q.status !== undefined && q.status !== null) params.set('status', String(q.status));
    if (q.region) params.set('region', q.region);
    if (q.assignedTo) params.set('assignedTo', q.assignedTo);
    if (q.role) params.set('role', q.role);
    if (q.pageSize) params.set('pageSize', String(q.pageSize));
    if (q.pageToken) params.set('pageToken', q.pageToken);

    const url = `${this.base}?${params.toString()}`;
    return this.request.get(url).pipe(
      map(r => r.body as ListEnquiriesResponseDto),
      map(res => ({
        items: (res.enquiries ?? []).map(this.mapEnquiry), // <- flat list → Enquiry
        nextPageToken: res.nextPageToken,
      })),
    );
  }

  getCounts(): Observable<any> {
    return this.request.get(`${this.base}/counts`).pipe(
      map(r => (r.body as any)),
    );
  }

  get(id: string): Observable<EnquiryDetail> {
    return this.request.get(`${this.base}/${id}`).pipe(
      map(r => this.mapEnquiryDetail(r.body)), // <- detail shape → EnquiryDetail
    );
  }

  create(payload: CreateEnquiryDto): Observable<Enquiry> {
    return this.request.post(this.base, payload).pipe(
      map(r => this.mapEnquiry(r.body)),
    );
  }

  update(id: string, dto: UpdateEnquiryDto): Observable<Enquiry> {
    return this.request.put(`${this.base}/${id}`, {
      ...dto,
      updateMask: dto.updateMask ? {paths: dto.updateMask.paths} : undefined,
    }).pipe(
      map(r => this.mapEnquiry(r.body)),
    );
  }

  createUser(payload: CreateUserDto): Observable<EnquiryDetail> {
    // backend returns the enriched detail after linking the user
    return this.request.post(`${this.base}/user`, payload).pipe(
      map(r => this.mapEnquiryDetail(r.body)),
    );
  }

  updateUser(payload: Partial<EnquiryUserUpdateInputDto>): Observable<EnquiryDetail> {
    // backend returns the enriched detail after linking the user
    return this.request.put(`${this.base}/user`, payload).pipe(
      map(r => this.mapEnquiryDetail(r.body)),
    );
  }

  delete(id: string): Observable<void> {
    return this.request.delete(`${this.base}/${id}`).pipe(map(() => void 0));
  }


  assignClinician(enquiryId: string, clinicianUserId: string): Observable<void> {
    return this.request.post(`${this.base}/assignClinician/${enquiryId}`, { clinicianUserId }).pipe(map(() => void 0));
  }

  bookAppointment(
    enquiryId: string,
    patientId: string,
    clinicianId: string,
    startTime: string,
    endTime: string,
  ): Observable<void> {
    const payload = { enquiryId, patientId, clinicianId, startTime, endTime }
    return this.request.post(`${this.base}/bookAppointment`, payload).pipe(map(() => void 0));
  }

  // ---------- mapping helpers ----------

  /** Normalize various timestamp shapes to a unix-seconds number. */
  private tsToSeconds(v: any): number {
    if (v == null) return 0;
    if (typeof v === 'number') return v > 10_000_000_000 ? Math.floor(v / 1000) : v;
    if (typeof v === 'string') return Math.floor(Date.parse(v) / 1000) || 0;
    if (typeof v === 'object') {
      if (typeof v.seconds === 'number') return v.seconds;
      if (v._seconds) return v._seconds; // some backends
    }
    return 0;
  }

  /** Map a raw server Enquiry (proto/DTO) → UI Enquiry. */
  private mapEnquiry = (raw: any): Enquiry => ({
    id: raw.id,
    userId: raw.userId,
    status: raw.status,
    region: raw.region,
    role: raw.role,
    preferredLanguage: raw.preferredLanguage,
    contactPhone: raw.contactPhone,
    contactEmail: raw.contactEmail,
    assignedTo: raw.assignedTo,
    createdAt: this.tsToSeconds(raw.createdAt),
    updatedAt: this.tsToSeconds(raw.updatedAt),
    metadata: raw.metadata ?? {},
    lastNote: raw.lastNote
      ? {
        id: raw.lastNote.id,
        createdBy: raw.lastNote.createdBy,
        content: raw.lastNote.content,
        systemNote: raw.lastNote.systemNote,
        createdAt: this.tsToSeconds(raw.lastNote.createdAt),
      }
      : undefined,
  });

  /**
   * Map a detail payload which may be either:
   *  - { enquiry, identityUser, userProfile }  (preferred)
   *  - a plain Enquiry (fallback)
   */
  private mapEnquiryDetail = (raw: any): EnquiryDetail => {
    return {
      ...raw
    };
  };
}
